import type Dropdown from '../dropdown/Dropdown.js'
import type Popout from '../popout/Popout.js'
import { html, LitElement } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { EventController } from '../common/controllers/EventController.js'
import componentStyle from '../common/styles/Component.css'
import style from './DropdownSubmenu.css'
import '../popout/Popout.js'

let popoutCounter = 0

/**
 * Dropdown submenu nests a secondary menu within a parent dropdown.
 *
 * @status ready
 * @category action
 *
 * @slot trigger - The element that opens the submenu (typically nord-dropdown-item).
 * @slot - The submenu content items.
 *
 * @fires {NordEvent} open - Forwarded from the internal nord-popout.
 * @fires {NordEvent} close - Forwarded from the internal nord-popout.
 * @fires {CustomEvent} nord-submenu-navigate - Fired when mobile stack navigation activates this submenu.
 *
 * @parent Dropdown
 */
@customElement('nord-dropdown-submenu')
export default class DropdownSubmenu extends LitElement {
  static styles = [componentStyle, style]

  @query('nord-popout')
  private popout?: Popout

  /** @internal */
  @state() private mobileActive = false

  private readonly popoutId = `nord-dropdown-submenu-popout-${++popoutCounter}`
  private events = new EventController(this)
  private currentTriggerElement: HTMLElement | null = null
  private popoutOpen = false
  private readonly mobileMediaQuery = window.matchMedia('(max-width: 35.9375em)')
  @state() private isSmallScreen = this.mobileMediaQuery.matches
  @state() private parentDropdownAlwaysFloating = false
  private parentObserver?: MutationObserver

  private get shouldUseMobileStack(): boolean {
    return this.isSmallScreen && !this.parentDropdownAlwaysFloating
  }

  private get parentDropdown(): Dropdown | null {
    return this.closest<Dropdown>('nord-dropdown')
  }

  private get isInSubmenuStack(): boolean {
    const parent = this.parentDropdown
    if (!parent)
      return false
    return parent.submenuStackRef.includes(this)
  }

  private get isDeepestInStack(): boolean {
    const parent = this.parentDropdown
    if (!parent)
      return false
    return parent.deepestSubmenu === this
  }

  connectedCallback() {
    super.connectedCallback()

    // Set aria-haspopup on trigger before first render
    const triggerElement = this.querySelector<HTMLElement>('[slot="trigger"]')
    triggerElement?.setAttribute('aria-haspopup', 'menu')

    // Read alwaysFloating from parent dropdown
    const parentDropdown = this.closest<Dropdown>('nord-dropdown')
    if (parentDropdown) {
      this.parentDropdownAlwaysFloating = parentDropdown.alwaysFloating
      // Close this submenu when a sibling regular item is hovered
      this.events.listen(parentDropdown, 'mouseover', this.handleParentDropdownMouseOver)

      // Observe always-floating attribute on parent dropdown for reactivity
      this.parentObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'always-floating') {
            this.parentDropdownAlwaysFloating = parentDropdown.alwaysFloating
          }
        }
      })
      this.parentObserver.observe(parentDropdown, {
        attributes: true,
        attributeFilter: ['always-floating'],
      })
    }

    // Listen for screen size changes (use correct breakpoint)
    this.events.listen(this.mobileMediaQuery, 'change', (event: MediaQueryListEvent) => {
      this.isSmallScreen = event.matches
      this.requestUpdate()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.parentObserver?.disconnect()
  }

  render() {
    // Mobile stack navigation: show content inline when active
    if (this.shouldUseMobileStack && this.mobileActive) {
      // Add active class for any submenu in the stack, and is-deepest-active for the deepest one
      return html`
        <slot name="trigger"></slot>
        <div class=${classMap({
          'n-dropdown-submenu-content': true,
          'active': this.isInSubmenuStack,
          'is-deepest-active': this.isDeepestInStack,
        })} @keydown=${this.handleContentKeydown}>
          <slot></slot>
        </div>
      `
    }

    // Desktop/always-floating mode: show popout
    return html`
      <slot
        name="trigger"
        aria-controls=${this.popoutId}
        @slotchange=${this.handleTriggerSlotChange}
      ></slot>
      <nord-popout
        id=${this.popoutId}
        position="inline-end"
        always-floating
        @open=${this.handlePopoutOpen}
        @close=${this.handlePopoutClose}
      >
        <div
          class="n-dropdown-submenu-content"
          @keydown=${this.handleContentKeydown}
        >
          <slot></slot>
        </div>
      </nord-popout>
    `
  }

  private handleTriggerSlotChange() {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="trigger"]')
    const triggerElement = slot?.assignedElements()[0] as HTMLElement | undefined

    // Remove listeners from previous trigger element
    if (this.currentTriggerElement && this.currentTriggerElement !== triggerElement) {
      this.currentTriggerElement.removeEventListener('mouseenter', this.handleTriggerMouseEnter)
      this.currentTriggerElement.removeEventListener('click', this.handleTriggerClick)
      this.currentTriggerElement.removeEventListener('keydown', this.handleTriggerKeydown)
    }

    this.currentTriggerElement = triggerElement ?? null

    if (!triggerElement)
      return

    triggerElement.setAttribute('aria-haspopup', 'menu')

    const isTouchDevice = !window.matchMedia('(hover: hover)').matches

    // Non-touch devices: hover to open
    this.events.listen(triggerElement, 'mouseenter', this.handleTriggerMouseEnter)
    // Non-touch devices or small screens: click to navigate/open
    if (!isTouchDevice || this.isSmallScreen) {
      this.events.listen(triggerElement, 'click', this.handleTriggerClick)
    }
    // Keyboard: arrow keys for navigation
    this.events.listen(triggerElement, 'keydown', this.handleTriggerKeydown)
  }

  private handleTriggerMouseEnter = () => {
    // On mobile, don't open popout on hover — only on click
    if (this.shouldUseMobileStack)
      return

    this.closeOtherSubmenus()
    this.popout?.show()
  }

  private handleTriggerClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopImmediatePropagation()

    if (this.shouldUseMobileStack) {
      this.navigateIntoSubmenu()
      return
    }

    this.closeOtherSubmenus()
    this.popout?.show()
  }

  private handleTriggerKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'Enter') {
      event.preventDefault()

      if (this.shouldUseMobileStack) {
        this.navigateIntoSubmenu()
        return
      }

      this.closeOtherSubmenus()
      this.popout?.show()
      this.updateComplete.then(() => {
        this.querySelector<HTMLElement>('nord-dropdown-item')?.focus()
      })
    }
  }

  private handleContentKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      event.stopPropagation()

      if (this.shouldUseMobileStack && this.mobileActive) {
        this.closest<Dropdown>('nord-dropdown')?.navigateBack()
        return
      }

      this.popout?.hide(true)
    }
  }

  private handlePopoutOpen = () => {
    this.popoutOpen = true
    this.closeOtherSubmenus()
  }

  private handlePopoutClose = (event: Event) => {
    event.stopPropagation()
    this.popoutOpen = false
  }

  private handleParentDropdownMouseOver = (event: MouseEvent) => {
    // Don't close on mouseover during mobile stack navigation
    if (this.shouldUseMobileStack)
      return

    const target = event.target as HTMLElement
    const isRegularDropdownItem = target.tagName === 'NORD-DROPDOWN-ITEM' && !this.contains(target)
    if (isRegularDropdownItem && this.popoutOpen) {
      event.stopImmediatePropagation()
      this.popout?.hide()
    }
  }

  private closeOtherSubmenus() {
    // Don't close siblings during mobile stack navigation
    if (this.shouldUseMobileStack)
      return

    // Find the immediate parent container (dropdown or submenu)
    const immediateParent = this.parentElement?.closest('nord-dropdown, nord-dropdown-submenu')
    if (!immediateParent)
      return

    // Close only siblings at the same level
    Array.from(
      immediateParent.querySelectorAll<DropdownSubmenu>(':scope > nord-dropdown-submenu'),
    ).forEach((sibling) => {
      if (sibling !== this)
        sibling.close()
    })
  }

  /**
   * Deactivate mobile stack navigation (remove from stack).
   * @internal
   */
  deactivateMobile() {
    this.mobileActive = false

    // NATIVELY REMOVE THE ATTRIBUTE FROM THE HOST
    this.removeAttribute('mobile-active')

    // Clear aria-expanded that may have been set by Popout during hover
    const triggerSlot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="trigger"]')
    const triggerElement = triggerSlot?.assignedElements()[0] as HTMLElement | undefined
    triggerElement?.removeAttribute('aria-expanded')
  }

  /**
   * Read trigger text for back button title in Dropdown.
   * @internal
   */
  get label(): string {
    const triggerSlot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="trigger"]')
    return triggerSlot?.assignedElements()[0]?.textContent?.trim() ?? ''
  }

  /**
   * Close the submenu programmatically.
   * Returns a promise that resolves when the close animation completes.
   */
  close(): Promise<TransitionEvent | void> {
    if (this.mobileActive) {
      this.deactivateMobile()
      return Promise.resolve()
    }

    if (!this.popout) {
      return Promise.reject(new Error('DropdownSubmenu: popout component not found or not ready'))
    }
    return this.popout.hide()
  }

  /**
   * Navigate into this submenu using mobile stack navigation.
   * @internal
   */
  private navigateIntoSubmenu() {
    this.mobileActive = true
    this.setAttribute('mobile-active', '')
    this.dispatchEvent(
      new CustomEvent('nord-submenu-navigate', { bubbles: true, detail: { submenu: this } }),
    )
    this.updateComplete.then(() => {
      // Focus on first submenu item (not back button in header)
      const contentDiv = this.querySelector('.n-dropdown-submenu-content')
      const firstItem = contentDiv?.querySelector<HTMLElement>('nord-dropdown-item')
      firstItem?.focus()
    })
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-dropdown-submenu': DropdownSubmenu
  }
}
